import React from 'react'
import { useResizableColumns, ResizeHandle, useSortableData, SortableHeader } from '../shared/tableUtils'

/**
 * DraftInvoices — port of Scale's DraftInvoices modal.
 *
 * Columns (Scale parity, scale-portal/.../HourlyInvoice/DraftInvoices.jsx):
 *   Invoice To | Total | Due Date | Created At | Action (edit, delete)
 *
 * Edit  — navigates to /hourly-billing/:case_id?draft_invoice=:uuid
 *         (resumes the multi-step Create Invoice form pre-populated).
 * Delete — window.confirm() then onDelete(id).
 */

function fmtMoney(n) {
    const v = Number(n) || 0
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(s) {
    if (!s) return '-'
    try {
        // YYYY-MM-DD parsed as local midnight to avoid TZ shift.
        const m = typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10).split('-') : null
        const d = m ? new Date(Number(m[0]), Number(m[1]) - 1, Number(m[2])) : new Date(s)
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch (_e) { return String(s) }
}

function resolveInvoiceTo(send_to) {
    if (!Array.isArray(send_to) || send_to.length === 0) return 'No recipient specified'
    if (send_to.length >= 2) return 'All Parties'
    return send_to[0]?.name || 'No recipient specified'
}

export default function DraftInvoices({ isOpen, onClose, invoices = [], onDelete, onEdit }) {
    const rows = Array.isArray(invoices) ? invoices : []

    const { widths: colWidths, setWidth: setColWidth } = useResizableColumns(
        'caseDetail.draftInvoices',
        { invoiceTo: 280, total: 130, dueDate: 160, createdAt: 160 }
    )
    const { sortedRows, sort: tableSort, requestSort: requestTableSort } = useSortableData(
        rows,
        {
            invoiceTo: (r) => resolveInvoiceTo(r.send_to),
            total: (r) => Number(r.subtotal || 0),
            dueDate: (r) => r.due_date || '',
            createdAt: (r) => r.created_at || '',
        }
    )

    if (!isOpen) return null

    const handleDelete = (id) => {
        // Scale uses window.confirm() — keep parity.
        // eslint-disable-next-line no-alert
        if (window.confirm('Are you sure you want to delete this draft?')) {
            onDelete?.(id)
        }
    }

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
        >
            {/* Scale parity: shouldCloseOnOverlayClick={false} — backdrop click
                does NOT dismiss; only the X button or Close button. */}
            <div
                className={`bg-white rounded-xl shadow-2xl w-full max-w-[1000px] mx-4 ${rows.length > 7 ? 'h-[75vh]' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h5 className="text-lg font-semibold">Draft Invoices</h5>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>

                <div className={`px-6 py-4 ${rows.length > 7 ? 'overflow-y-auto' : ''}`} style={rows.length > 7 ? { maxHeight: 'calc(75vh - 64px - 64px)' } : {}}>
                    {rows.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-12">No drafts yet.</p>
                    ) : (
                        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                            <thead className="text-xs uppercase text-gray-500 text-left border-b border-gray-200">
                                <tr>
                                    <th className="py-2 pr-3 relative" style={{ width: colWidths.invoiceTo }}>
                                        <SortableHeader sortKey="invoiceTo" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase text-gray-500">Invoice To</SortableHeader>
                                        <ResizeHandle onResize={setColWidth('invoiceTo')} getCurrentWidth={() => colWidths.invoiceTo} />
                                    </th>
                                    <th className="py-2 pr-3 relative" style={{ width: colWidths.total }}>
                                        <SortableHeader sortKey="total" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase text-gray-500">Total</SortableHeader>
                                        <ResizeHandle onResize={setColWidth('total')} getCurrentWidth={() => colWidths.total} />
                                    </th>
                                    <th className="py-2 pr-3 relative" style={{ width: colWidths.dueDate }}>
                                        <SortableHeader sortKey="dueDate" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase text-gray-500">Due Date</SortableHeader>
                                        <ResizeHandle onResize={setColWidth('dueDate')} getCurrentWidth={() => colWidths.dueDate} />
                                    </th>
                                    <th className="py-2 pr-3 relative" style={{ width: colWidths.createdAt }}>
                                        <SortableHeader sortKey="createdAt" sort={tableSort} onSort={requestTableSort} className="text-xs uppercase text-gray-500">Created At</SortableHeader>
                                        <ResizeHandle onResize={setColWidth('createdAt')} getCurrentWidth={() => colWidths.createdAt} />
                                    </th>
                                    <th className="py-2 pr-3 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((inv) => (
                                    <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-3 pr-3 text-gray-800">{resolveInvoiceTo(inv.send_to)}</td>
                                        <td className="py-3 pr-3 text-gray-800">${fmtMoney(inv.subtotal)}</td>
                                        <td className="py-3 pr-3 text-gray-800">{fmtDate(inv.due_date)}</td>
                                        <td className="py-3 pr-3 text-gray-800">{fmtDate(inv.created_at)}</td>
                                        <td className="py-3 pr-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {onEdit && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onEdit(inv)}
                                                        title="Edit"
                                                        className="p-1.5 rounded hover:bg-teal-50 text-[#115E59]"
                                                    >
                                                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path d="M14.69 2.66l2.65 2.65-9.5 9.5H5.19v-2.65l9.5-9.5z"/></svg>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(inv.id)}
                                                    title="Delete"
                                                    className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-700"
                                                >
                                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}
